import type { CutoutDefinition } from "@project/emerge";
import { AbstractPartInteraction } from "@project/emerge";
import {
  sideToNormalVector,
  sideToUpVector,
  vectorToSide,
} from "@project/emerge/src/utils/RotationUtils";
import type { AbstractPartDefinition } from "@project/emerge-types";
import { Sides } from "@project/emerge-types";
import { Box3, Quaternion, Vector3 } from "three";
import { LTPartType } from "../enum/PartType";
import type { ExtendedCutoutDefinition } from "../interfaces/CutoutProperties";
import type { FlightCaseBlueprint } from "../interfaces/FlightCaseBlueprint";
import type { FlightCaseSettings } from "../interfaces/FlightCaseSettings";
import type { OuterPanelProperties } from "../interfaces/OuterPanelProperties";
import type { PadDefinition } from "../interfaces/PadProperties";

/**
 * Interaction that adds cutouts to outer panels and foam pads from any SupplyPart that has cutout information
 * This includes: Handles, Locks, Hinges, Rollers, TrolleyHandles, Dishes, Foot, etc.
 */
export class SupplyPartPanelInteraction extends AbstractPartInteraction<
  FlightCaseBlueprint,
  FlightCaseSettings,
  AbstractPartDefinition<LTPartType>
> {
  // Liste der SupplyPart-Typen, die möglicherweise Cutouts haben können
  private readonly supplyPartTypes = [
    LTPartType.Handle,
    LTPartType.Lock,
    LTPartType.Hinge,
    LTPartType.PianoHinge,
    LTPartType.Roller,
    LTPartType.TrolleyHandle,
    LTPartType.Dish,
    LTPartType.Foot,
    LTPartType.Corner,
    LTPartType.CornerBrace,
  ];

  constructor() {
    super("SupplyPartPanelInteraction");
  }

  /**
   * Extract cutouts from a supply part (locks, hinges, etc.)
   * Cutouts are now stored directly in partSelection.cutouts as an array
   */
  private extractCutoutsFromPart(
    part: AbstractPartDefinition<LTPartType>,
  ): CutoutDefinition[] {
    // Try to access partSelection safely
    const partWithSelection = part as {
      partSelection?: {
        cutouts?: CutoutDefinition[];
      };
    };

    const partSelection = partWithSelection.partSelection;
    if (!partSelection) {
      return [];
    }

    // Cutouts are now always stored as an array in partSelection.cutouts
    if (Array.isArray(partSelection.cutouts)) {
      return [...partSelection.cutouts];
    }

    return [];
  }

  /**
   * Check if a cutout already exists in the panel's cutouts array
   */
  private isCutoutDuplicate(
    cutout: ExtendedCutoutDefinition,
    target: OuterPanelProperties | PadDefinition,
  ): boolean {
    return (
      target.cutouts?.some(
        (existing) =>
          cutout.path === existing.path &&
          cutout.position.x === existing.position.x &&
          cutout.position.y === existing.position.y &&
          cutout.position.z === existing.position.z &&
          cutout.side === existing.side &&
          (cutout.orientation ?? 0) === (existing.orientation ?? 0),
      ) ?? false
    );
  }

  /** In-plane size (same convention as {@link OuterPanelProperties.size}). */
  private getFacePlaneSize(target: OuterPanelProperties | PadDefinition): {
    x: number;
    y: number;
  } {
    if (target.type === LTPartType.Panel) {
      const p = target as OuterPanelProperties;
      return { x: p.size.x, y: p.size.y };
    }
    const pad = target as PadDefinition;
    return { x: pad.size[0], y: pad.size[1] };
  }

  /**
   * Execute the interaction between supply parts and panels
   * Diese Methode identifiziert alle relevanten Supply Parts mit Cutouts
   * und wendet diese auf passende Panels an.
   */
  public async execute(
    _blueprint: FlightCaseBlueprint,
    _settings: FlightCaseSettings,
    parts: AbstractPartDefinition<LTPartType>[],
  ): Promise<number> {
    let modifiedCount = 0;

    // Alle Panels finden (nur OuterPanels)
    const panels = parts
      .filter((part) => part.type === LTPartType.Panel)
      .map((part) => part as unknown as OuterPanelProperties);

    const pads = parts
      .filter((part) => part.type === LTPartType.Pad)
      .map((part) => part as unknown as PadDefinition);

    // Zuerst Cutout-Listen zurücksetzen (leeren)
    modifiedCount += this.resetPanelAndPadCutouts(panels, pads);

    // Sammle alle SupplyParts, unabhängig vom Typ
    const allSupplyParts = this.collectSupplyPartsWithCutouts(parts);

    // Alle möglichen Panel-SupplyPart-Interaktionen durchgehen
    for (const { part: supplyPart, storedCutouts } of allSupplyParts) {
      try {
        modifiedCount += this.processSupplyPartCutouts(
          supplyPart,
          storedCutouts,
          panels,
          pads,
        );
      } catch (error) {
        console.error(
          `Error processing cutout for part ${supplyPart.type}:`,
          error,
        );
      }
    }

    return modifiedCount;
  }

  /**
   * Reset cutouts for all panels and pads; return the count of removed cutouts
   */
  private resetPanelAndPadCutouts(
    panels: OuterPanelProperties[],
    pads: PadDefinition[],
  ): number {
    let removedCount = 0;
    for (const panel of panels) {
      const oldLength = panel.cutouts?.length || 0;
      panel.cutouts = [];
      if (oldLength > 0) {
        removedCount += oldLength;
      }
    }
    for (const pad of pads) {
      const oldLength = pad.cutouts?.length || 0;
      pad.cutouts = [];
      if (oldLength > 0) {
        removedCount += oldLength;
      }
    }
    return removedCount;
  }

  /**
   * Collect all supply parts that have cutouts
   */
  private collectSupplyPartsWithCutouts(
    parts: AbstractPartDefinition<LTPartType>[],
  ): Array<{
    part: AbstractPartDefinition<LTPartType>;
    storedCutouts: CutoutDefinition[];
  }> {
    const allSupplyParts: Array<{
      part: AbstractPartDefinition<LTPartType>;
      storedCutouts: CutoutDefinition[];
    }> = [];

    for (const partType of this.supplyPartTypes) {
      const supplyParts = parts.filter((part) => part.type === partType);
      supplyParts.forEach((part) => {
        const cutouts = this.extractCutoutsFromPart(part);
        if (cutouts.length > 0) {
          allSupplyParts.push({
            part,
            storedCutouts: cutouts,
          });
        }
      });
    }

    return allSupplyParts;
  }

  /**
   * Process cutouts for a single supply part and apply them to matching panels
   */
  private processSupplyPartCutouts(
    supplyPart: AbstractPartDefinition<LTPartType>,
    storedCutouts: CutoutDefinition[],
    panels: OuterPanelProperties[],
    pads: PadDefinition[],
  ): number {
    let modifiedCount = 0;

    // Transform all cutouts relative to part rotation
    const partQuat = new Quaternion().setFromEuler(supplyPart.rotation);
    const transformedCutouts = this.transformCutouts(storedCutouts, partQuat);

    for (const panel of panels) {
      modifiedCount += this.applyCutoutsToFace(
        supplyPart,
        transformedCutouts,
        panel,
      );
    }
    for (const pad of pads) {
      modifiedCount += this.applyCutoutsToFace(
        supplyPart,
        transformedCutouts,
        pad,
      );
    }

    return modifiedCount;
  }

  /**
   * Transform cutouts based on part rotation
   */
  private transformCutouts(
    cutouts: CutoutDefinition[],
    partQuat: Quaternion,
  ): Array<{
    original: CutoutDefinition;
    finalSide: Sides;
    finalOrientation: number;
  }> {
    return (cutouts || []).map((cutout) => {
      const initialCutoutSide = cutout.side ?? Sides.TOP;
      const { finalSide, finalOrientation } =
        this.calculateCutoutTransformation(initialCutoutSide, 0, partQuat);

      return {
        original: cutout,
        finalSide,
        finalOrientation,
      };
    });
  }

  /**
   * Apply matching cutouts to an outer panel or a pad (shared plane / strength filter logic).
   */
  private applyCutoutsToFace(
    supplyPart: AbstractPartDefinition<LTPartType>,
    transformedCutouts: Array<{
      original: CutoutDefinition;
      finalSide: Sides;
      finalOrientation: number;
    }>,
    target: OuterPanelProperties | PadDefinition,
  ): number {
    if (transformedCutouts.length === 0) {
      return 0;
    }

    const matchingTransformed = transformedCutouts.filter(
      ({ original, finalSide }) => {
        if (!this.isCutoutTouchingFace(supplyPart, target, original)) {
          return false;
        }
        // Stärke-Filter (Platten- bzw. Polster-Stärke)
        if (original.panelFilter != null) {
          const cutoutFilter = Number(original.panelFilter);
          if (Number.isNaN(cutoutFilter)) {
            return false;
          }
          if (target.strength !== cutoutFilter) {
            return false;
          }
        }
        return finalSide === target.side;
      },
    );

    let addedCount = 0;
    for (const {
      original,
      finalSide,
      finalOrientation,
    } of matchingTransformed) {
      const cutoutForFace: ExtendedCutoutDefinition = {
        ...original,
        position: { ...supplyPart.position },
        path: original.path,
        orientation: finalOrientation,
        side: finalSide,
      };

      if (!this.isCutoutDuplicate(cutoutForFace, target)) {
        target.cutouts.push(cutoutForFace);
        addedCount++;
      }
    }

    return addedCount;
  }

  /**
   * Bounding box for a rectangular face (outer panel or pad) with tolerance in-plane.
   * Stärkenachse: `halfStrength + cutDepthMm` wenn cutDepthMm > 0, sonst
   * `halfStrength + planeToleranzMm` (kein +50 zusätzlich zur Frästiefe).
   */
  private calculateFaceBoundingBox(
    target: OuterPanelProperties | PadDefinition,
    planeToleranzMm: number,
    cutDepthMm = 0,
  ): Box3 {
    const plane = this.getFacePlaneSize(target);
    const halfWidth = plane.x / 2;
    const halfHeight = plane.y / 2;
    const halfStrength = (target.strength || 19) / 2;

    const strengthPaddingMm =
      cutDepthMm > 0 && Number.isFinite(cutDepthMm)
        ? cutDepthMm
        : planeToleranzMm;
    const halfStrengthReach = halfStrength + strengthPaddingMm;

    const min = new Vector3().copy(target.position);
    const max = new Vector3().copy(target.position);

    switch (target.side) {
      case Sides.TOP:
      case Sides.BOTTOM:
        min.x -= halfWidth + planeToleranzMm;
        max.x += halfWidth + planeToleranzMm;
        min.y -= halfStrengthReach;
        max.y += halfStrengthReach;
        min.z -= halfHeight + planeToleranzMm;
        max.z += halfHeight + planeToleranzMm;
        break;

      case Sides.FRONT:
      case Sides.BACK:
        min.x -= halfWidth + planeToleranzMm;
        max.x += halfWidth + planeToleranzMm;
        min.y -= halfHeight + planeToleranzMm;
        max.y += halfHeight + planeToleranzMm;
        min.z -= halfStrengthReach;
        max.z += halfStrengthReach;
        break;

      case Sides.LEFT:
      case Sides.RIGHT:
        min.x -= halfStrengthReach;
        max.x += halfStrengthReach;
        min.y -= halfHeight + planeToleranzMm;
        max.y += halfHeight + planeToleranzMm;
        min.z -= halfWidth + planeToleranzMm;
        max.z += halfWidth + planeToleranzMm;
        break;

      default:
        break;
    }

    return new Box3(min, max);
  }

  /**
   * Berechnet die finale Seite und Rotation eines Cutouts basierend auf der
   * gesamten Part-Rotation (Quaternion) und der Cutout-Eigenrotation.
   */
  private calculateCutoutTransformation(
    cutoutSide: Sides,
    cutoutOrientation: number,
    partRotation: Quaternion,
  ): { finalSide: Sides; finalOrientation: number } {
    // 1. Gesamtrotation des Parts als Quaternion
    const finalQuaternion = partRotation;

    // 2. Cutout-Normale (z.B. LEFT = (-1, 0, 0))
    const cutoutNormal = sideToNormalVector(cutoutSide);

    // 3. Rotations-Quaternion um Cutout-Normale (cutoutOrientation in Grad)
    const cutoutQuaternion = new Quaternion().setFromAxisAngle(
      cutoutNormal,
      (cutoutOrientation * Math.PI) / 180,
    );

    // 4. Kombinierte Gesamtrotation
    const totalQuaternion = finalQuaternion.clone().multiply(cutoutQuaternion);

    // 5. Neue Cutout-Normale nach Rotation
    const rotatedNormal = cutoutNormal.clone().applyQuaternion(totalQuaternion);
    const finalSide = vectorToSide(rotatedNormal);

    // 6. Bestimme "Up"-Vektor des Cutouts in Ursprungsseite
    const originalUp = sideToUpVector(cutoutSide); // z.B. (0, 1, 0)

    // 7. Rotiere diesen Vektor mit der Gesamtrotation
    const rotatedUp = originalUp.clone().applyQuaternion(totalQuaternion);

    // 8. Hole Ziel-"Up"-Vektor auf finalSide
    const sideUp = sideToUpVector(finalSide);
    const sideNormal = sideToNormalVector(finalSide);

    // 9. Berechne Winkel zwischen rotatedUp und targetUp um die neue Normale
    const cross = new Vector3().crossVectors(sideUp, rotatedUp);
    const dot = sideUp.dot(rotatedUp);
    const angleRad = -Math.atan2(cross.dot(sideNormal), dot);
    const finalOrientation = ((angleRad * 180) / Math.PI + 360) % 360;

    return { finalSide, finalOrientation };
  }

  /**
   * Pro Cutout: Supply-Position muss in der Platten-/Polster-Box liegen.
   * Stärkenachse: nur `cut` (mm), ohne +planeToleranz; fehlt cut oder 0 → planeToleranzMm.
   */
  private isCutoutTouchingFace(
    supplyPart: AbstractPartDefinition<LTPartType>,
    target: OuterPanelProperties | PadDefinition,
    cutout: CutoutDefinition,
  ): boolean {
    const planeToleranzMm = 50;
    const cutMm =
      typeof cutout.cut === "number" &&
      Number.isFinite(cutout.cut) &&
      cutout.cut > 0
        ? cutout.cut
        : 0;

    const faceBox = this.calculateFaceBoundingBox(
      target,
      planeToleranzMm,
      cutMm,
    );

    const touches = faceBox.containsPoint(supplyPart.position);

    return touches;
  }
}
